package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.PrimaryTabRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.datasource.chapterOne
import com.shadowings.gero.data.datasource.defaultChapters
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.correctable
import com.shadowings.gero.presentation.model.LandingUiState
import com.shadowings.gero.presentation.model.UiLessonNode
import com.shadowings.gero.presentation.model.UiLessonRow
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DevicePreviews
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.tab_chapters
import gero.composeapp.generated.resources.tab_correggi
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

private val previewLearnUserData = UserData(
    explanations = setOf(LessonIds.INTRO_HIEROGLYPHS, LessonIds.INTRO_HIEROGLYPHS_2),
    stars = mapOf(LessonIds.INTRO_HIEROGLYPHS to 3),
    tutorialDone = true,
    wrongAnswers = previewWrongAnswers
)

private val previewLearnUiState = LandingUiState(
    chapters = defaultChapters,
    userData = previewLearnUserData,
    lessonRowsPerChapter = defaultChapters.associate { chapter ->
        chapter.number to listOf(
            UiLessonRow(
                lessons = chapterOne.map { lesson ->
                    UiLessonNode(
                        lesson = lesson,
                        isLocked = !previewLearnUserData.explanations.contains(lesson.id),
                        earnedStars = previewLearnUserData.stars[lesson.id] ?: 0,
                        isPerfect = (previewLearnUserData.stars[lesson.id] ?: 0) == 3
                    )
                }
            )
        )
    }
)

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun LearnScreenChaptersPreview() {
    AppTheme {
        Surface {
            LearnScreen(
                uiState = previewLearnUiState,
                onExplanationClick = {},
                onQuizClick = {},
                starsNeeded = emptyMap()
            )
        }
    }
}

@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun LearnScreenCorreggiPreview() {
    AppTheme {
        Surface {
            LearnScreen(
                uiState = previewLearnUiState,
                onExplanationClick = {},
                onQuizClick = {},
                starsNeeded = emptyMap(),
                initialTab = 1
            )
        }
    }
}

/**
 * The "Impara" bottom-bar destination: a tab row (same style as Compendio/Personaggi) with
 * 0 Capitoli (the lesson list) and 1 Correggi (the mistakes review list).
 */
@Composable
fun LearnScreen(
    uiState: LandingUiState,
    onExplanationClick: (String) -> Unit,
    onQuizClick: (String) -> Unit,
    starsNeeded: Map<String, Int>,
    modifier: Modifier = Modifier,
    onTabSelected: (Int) -> Unit = {},
    initialTab: Int = 0
) {
    val hapticFeedback = LocalHapticFeedback.current
    // rememberSaveable: tornando da un quiz di correzione si resta sulla tab Correggi.
    var selectedTab by rememberSaveable { mutableIntStateOf(initialTab) }
    // .correctable() drops MULTI_SELECTION/MULTI_PRESS mistakes — they're never rebuilt into a
    // correction question (see WrongAnswerRecordQuizMapper.kt), so they're excluded from this
    // list (and from the Correggi badge count) rather than shown but uncorrectable.
    val correctableWrongAnswers = remember(uiState.userData.wrongAnswers) {
        uiState.userData.wrongAnswers.correctable()
    }
    val tabs: List<Pair<StringResource, String>> = listOf(
        Res.string.tab_chapters to "learn_tab_chapters",
        Res.string.tab_correggi to "learn_tab_correggi",
    )

    Column(modifier = modifier) {
        PrimaryTabRow(
            selectedTabIndex = selectedTab,
            modifier = Modifier.fillMaxWidth().semantics { testTag = "learn_tab_row" }
        ) {
            tabs.forEachIndexed { index, (title, tag) ->
                Tab(
                    selected = selectedTab == index,
                    onClick = {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        selectedTab = index
                        onTabSelected(index)
                    },
                    modifier = Modifier.semantics { testTag = tag },
                    text = {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(4.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text(
                                text = stringResource(title),
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis,
                                fontWeight = if (selectedTab == index) FontWeight.Bold else FontWeight.Normal
                            )
                            if (index == 1 && correctableWrongAnswers.isNotEmpty()) {
                                Text(
                                    text = "(${correctableWrongAnswers.size})",
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    fontWeight = if (selectedTab == index) FontWeight.Bold else FontWeight.Normal
                                )
                            }
                        }
                    }
                )
            }
        }
        when (selectedTab) {
            0 -> ChaptersScreen(
                uiState = uiState,
                onExplanationClick = onExplanationClick,
                onQuizClick = onQuizClick,
                starsNeeded = starsNeeded,
                modifier = Modifier.fillMaxSize()
            )

            else -> ErroriListContent(
                userData = uiState.userData,
                wrongAnswers = correctableWrongAnswers,
                modifier = Modifier.fillMaxSize(),
                onCorreggiClick = { onQuizClick(LessonIds.CORRECTION) }
            )
        }
    }
}
